/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useCallback, useState, useContext } from 'react'
import { useServiceContext } from '../../../root'
import { getAllWatermarks } from '../../../services/api/watermarks'
import { RcmTableDataWrapper } from '../../../components/rcm-table/RcmTableDataWrapper'
import { RcmTableDataContext } from '../../../components/rcm-table/RcmTableDataContext'
import RcmTableFilterTrigger from '../../../components/rcm-table/RcmTableFilterTrigger'
import Loader from '../../../components/shared/Loader'
import PageMenu from './PageMenu'
import TableFilter from './TableFilter'
import Table from './Table'
import ModalEdit from './Actions/ModalEdit'

const WatermarksContent: React.FC = () => {
  const tableContext = useContext(RcmTableDataContext)
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [editingWatermark, setEditingWatermark] = useState<any>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const handleOpenModal = (watermark?: any) => {
    setEditingWatermark(watermark || null)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingWatermark(null)
  }

  return (
    <>
      <Loader blocking={isExporting}>
        <div className="row">
          <div className="col-xs-12">
            <div className="d-flex flex-wrap" style={{ paddingBottom: '1%' }}>
              <h1 className="align-left align-top" id="2d10e850-9984-4d15-ad97-dc27960ff86e" style={{ margin: '0' }}>
                {translate('WATERMARKS_TITLE')}
              </h1>

              <div className="align-right align-bottom d-flex">
                <PageMenu
                  onAddWatermark={() => handleOpenModal()}
                  onExportStart={() => setIsExporting(true)}
                  onExportEnd={() => setIsExporting(false)}
                />
                <RcmTableFilterTrigger />
              </div>
            </div>

            <TableFilter />
            <Table
              onEditWatermark={handleOpenModal}
              onDeleteSuccess={() => tableContext?.refreshTable()}
              onExportStart={() => setIsExporting(true)}
              onExportEnd={() => setIsExporting(false)}
            />
          </div>
        </div>
        {isModalOpen && <ModalEdit watermark={editingWatermark || undefined} onClose={handleCloseModal} />}
      </Loader>
    </>
  )
}

const WatermarksPage: React.FC = () => {
  // Create loadData function for RcmTableDataWrapper
  const loadData = useCallback(async (pagingParams: any, filter: any) => {
    try {
      const typeIds = filter?.types ? filter.types.map((t: any) => t.id || t.value || t) : []
      const result = await getAllWatermarks({
        pageStart: pagingParams.pageStart || 0,
        pageSize: pagingParams.pageSize || 15,
        sortBy: pagingParams.sortBy || 'NAME',
        sortDesc: pagingParams.sortDesc !== undefined ? pagingParams.sortDesc : false,
        ...(typeIds.length > 0 ? { id: typeIds } : {})
      })
      return {
        elements: result.elements || [],
        totalElements: result.totalElements || 0
      }
    } catch (error) {
      return {
        elements: [],
        totalElements: 0
      }
    }
  }, [])

  return (
    <div className="mcc-content-frame theme-sandman">
      <RcmTableDataWrapper
        loadData={loadData}
        defaultSortBy="NAME"
        defaultSortDesc={false}
        persistenceKey="settingsWatermarks"
        doPreload={true}
      >
        <WatermarksContent />
      </RcmTableDataWrapper>
    </div>
  )
}

export default WatermarksPage
